import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Building2, Search } from 'lucide-react';
import { api } from '../lib/api';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

const emptyForm = {
  business_name: '',
  enterprise_number: '',
  jurisdiction: 'BE',
  company_type: '',
  registered_address: '',
  website: '',
  contact_email: '',
  phone: '',
};

function comparable(value) {
  return String(value || '').trim();
}

function comparableEnterpriseNumber(value) {
  return comparable(value).replace(/^BE/i, '').replace(/[.\s-]/g, '');
}

function fmt(value) {
  return value || '-';
}

function fmtDate(value) {
  return value ? new Date(value).toLocaleString() : '-';
}

function registryStatusLabel(value) {
  if (value === 'active') return 'Active';
  if (value === 'inactive') return 'Inactive';
  return 'Unknown';
}

function SourceBadge({ source }) {
  const value = String(source || 'manual').toLowerCase() === 'kbo' ? 'KBO' : 'MANUAL';
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 uppercase tracking-widest text-[10px] font-mono border border-zinc-200 bg-zinc-50 text-zinc-600">
      {value}
    </span>
  );
}

function PreviewField({ label, value, mono = false }) {
  return (
    <div className="grid gap-1 border-b border-zinc-100 py-3 sm:grid-cols-[11rem_minmax(0,1fr)]">
      <div className="text-xs uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`${mono ? 'font-mono text-xs' : 'text-sm'} min-w-0 break-words text-zinc-900`}>{fmt(value)}</div>
    </div>
  );
}

export default function BusinessVerificationNew() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefillEnterpriseNumber = location.state?.enterprise_number;
  const prefillHandledRef = useRef(false);
  const [form, setForm] = useState(emptyForm);
  const [lookupResult, setLookupResult] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [workspaceLocked, setWorkspaceLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lookupMessage, setLookupMessage] = useState(null);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const applyLookupResult = useCallback((data) => {
    const registryFetchedAt = new Date().toISOString();
    const nextLookup = { ...data, registry_fetched_at: registryFetchedAt };
    setLookupResult(nextLookup);
    setManualMode(false);
    setLookupMessage({ tone: 'success', text: 'Official company details found. Review the registry snapshot before creating the KYB record.' });
    setForm((current) => ({
      ...current,
      enterprise_number: data.formatted_enterprise_number || data.enterprise_number || current.enterprise_number,
      business_name: data.business_name || current.business_name,
      jurisdiction: data.jurisdiction || current.jurisdiction || 'BE',
      company_type: data.company_type || current.company_type,
      registered_address: data.registered_address || current.registered_address,
      website: data.website || current.website,
      contact_email: data.contact_email || current.contact_email,
      phone: data.phone || current.phone,
    }));
  }, []);

  const lookupCompany = useCallback(async (enterpriseNumberOverride = null) => {
    const enterpriseNumber = enterpriseNumberOverride || form.enterprise_number;
    setLookupLoading(true);
    setLookupMessage(null);
    setError(null);
    try {
      const { data } = await api.get('/business-verifications/lookup', {
        params: { enterprise_number: enterpriseNumber },
      });
      if (data.status === 'not_found') {
        setLookupMessage({ tone: 'neutral', text: 'No Belgian company found for this enterprise number.' });
        setLookupResult(null);
        return;
      }
      if (data.status === 'invalid_enterprise_number') {
        setLookupMessage({ tone: 'error', text: 'Enter a valid Belgian enterprise number with 10 digits.' });
        setLookupResult(null);
        return;
      }
      applyLookupResult(data);
    } catch (e) {
      const code = e?.response?.data?.error;
      setLookupMessage({
        tone: 'error',
        text: code === 'invalid_enterprise_number'
          ? 'Enter a valid Belgian enterprise number with 10 digits.'
          : 'Registry lookup unavailable. You can continue manually.',
      });
    } finally {
      setLookupLoading(false);
    }
  }, [applyLookupResult, form.enterprise_number]);

  useEffect(() => {
    if (!prefillEnterpriseNumber || prefillHandledRef.current) return;
    prefillHandledRef.current = true;
    setForm((current) => ({ ...current, enterprise_number: prefillEnterpriseNumber }));
    lookupCompany(prefillEnterpriseNumber);
  }, [prefillEnterpriseNumber, lookupCompany]);

  const searchCompanies = async () => {
    const query = searchQ.trim();
    if (!query || searchLoading) return;
    setSearchLoading(true);
    setSearchAttempted(true);
    setSearchError(false);
    setSearchResults([]);
    try {
      const { data } = await api.get('/business-verifications/search', { params: { q: query } });
      setSearchResults(Array.isArray(data) ? data : []);
    } catch {
      setSearchError(true);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const hasManualOverrides = () => {
    if (!lookupResult) return false;
    const comparisons = [
      ['business_name', lookupResult.business_name],
      ['jurisdiction', lookupResult.jurisdiction],
      ['company_type', lookupResult.company_type],
      ['registered_address', lookupResult.registered_address],
    ];
    const textChanged = comparisons.some(([field, registryValue]) => comparable(form[field]) !== comparable(registryValue));
    const enterpriseChanged = comparableEnterpriseNumber(form.enterprise_number) !== comparableEnterpriseNumber(lookupResult.enterprise_number);
    return textChanged || enterpriseChanged;
  };

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setWorkspaceLocked(false);
    try {
      const usedLookup = Boolean(lookupResult);
      const { data } = await api.post('/business-verifications', {
        ...form,
        source: usedLookup ? 'kbo' : 'manual',
        registry_source: usedLookup ? lookupResult.registry_source || 'kbo' : null,
        registry_fetched_at: usedLookup ? lookupResult.registry_fetched_at : null,
        registry_payload: usedLookup ? lookupResult : {},
        has_manual_overrides: usedLookup ? hasManualOverrides() : false,
      });
      navigate(`/dashboard/businesses/${data.business_verification.id}`);
    } catch (e) {
      const code = e?.response?.data?.code || e?.response?.data?.error;
      setWorkspaceLocked(code === 'workspace_verification_required');
      setError(code === 'workspace_verification_required'
        ? 'Verify your identity to activate this workspace and unlock the free tier.'
        : code === 'missing_business_name'
        ? 'Enter the business name.'
        : 'Could not create the business verification.');
    } finally {
      setLoading(false);
    }
  };

  const selectSearchResult = (result) => {
    setSearchQ(result.business_name || result.formatted_enterprise_number || result.enterprise_number);
    lookupCompany(result.enterprise_number);
  };

  const resetSelection = () => {
    setLookupResult(null);
    setForm(emptyForm);
    setSearchQ('');
    setSearchResults([]);
    setSearchAttempted(false);
    setSearchError(false);
    setLookupMessage(null);
    setError(null);
  };

  return (
    <div data-testid="business-verification-new-page">
      <VerificationBreadcrumb rootLabel="Businesses" rootTo="/dashboard/businesses" current="New verification"/>

      <div className="mx-auto max-w-3xl space-y-6">
        <section className="text-center">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Verification setup</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-2">New business verification</h1>
          <p className="mx-auto mt-2 max-w-xl text-sm text-zinc-500">
            Start from official Belgian registry data, then create a KYB verification record for review.
          </p>
        </section>

        {!lookupResult && !manualMode && (
          <section className="mx-auto max-w-2xl border border-zinc-200 rounded-sm bg-white p-6">
            <div>
              <h2 className="font-display text-lg font-semibold">Search Belgian company</h2>
              <p className="text-sm text-zinc-500 mt-1">Look up the Belgian KBO/BCE registry by company name or enterprise number.</p>
            </div>
            <div className="mt-5">
              <div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="h-11 flex min-w-0 flex-1 items-center gap-2 border border-zinc-200 rounded-sm bg-white px-3">
                    <Search className="w-4 h-4 text-zinc-400" strokeWidth={1.5}/>
                    <input value={searchQ}
                      onChange={(event) => {
                        setSearchQ(event.target.value);
                        setSearchAttempted(false);
                        setSearchError(false);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          searchCompanies();
                        }
                      }}
                      className="h-full min-w-0 flex-1 bg-transparent text-sm text-zinc-800 focus:outline-none placeholder:text-zinc-400"
                      placeholder="Search by company name or enterprise number"/>
                  </div>
                  <button type="button" onClick={searchCompanies} disabled={!searchQ.trim() || searchLoading}
                    className="h-11 shrink-0 px-4 text-sm bg-black text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed">
                    Search company
                  </button>
                </div>

                {searchLoading && (
                  <div className="mt-4 border border-zinc-200 bg-white px-3 py-3 text-sm text-zinc-600">
                    Searching Belgian KBO/BCE registry…
                  </div>
                )}

                {!searchLoading && searchError && (
                  <div className="mt-4 border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
                    Could not search the registry. Try again or create manually.
                  </div>
                )}

                {!searchLoading && !searchError && searchAttempted && searchResults.length === 0 && (
                  <div className="mt-4 border border-zinc-200 bg-white px-3 py-3">
                    <p className="text-sm font-medium text-zinc-800">No companies found.</p>
                    <p className="mt-1 text-sm text-zinc-500">Try another name or enterprise number, or create the record manually.</p>
                  </div>
                )}

                {!searchLoading && !searchError && searchResults.length > 0 && (
                  <div className="mt-4 border border-zinc-200 rounded-sm bg-white">
                    {searchResults.map((result) => (
                      <button key={result.enterprise_number} type="button"
                        onClick={() => selectSearchResult(result)}
                        className="block w-full border-b border-zinc-100 px-3 py-3 text-left last:border-b-0 hover:bg-zinc-50">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-zinc-900">{result.business_name}</div>
                            <div className="mt-1 font-mono text-xs text-zinc-500">{result.formatted_enterprise_number || result.enterprise_number}</div>
                          </div>
                          <SourceBadge source={result.source}/>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {lookupMessage && (
                <p className={`mt-3 text-sm ${lookupMessage.tone === 'error' ? 'text-red-700' : lookupMessage.tone === 'success' ? 'text-emerald-700' : 'text-zinc-500'}`}>
                  {lookupMessage.text}
                </p>
              )}

              <div className="mt-5 text-center">
                <button type="button" onClick={() => { setManualMode(true); setLookupMessage(null); }}
                  className="text-sm font-medium underline decoration-zinc-300 underline-offset-2 hover:text-zinc-600">
                  Create manually instead
                </button>
              </div>
            </div>
          </section>
        )}

        {lookupResult && (
          <form onSubmit={submit} className="space-y-4">
            <section className="border border-zinc-200 rounded-sm bg-white p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center border border-zinc-200 rounded-sm">
                    <Building2 className="h-4 w-4 text-zinc-500" strokeWidth={1.5}/>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Official registry preview</p>
                    <h2 className="mt-1 font-display text-xl font-semibold break-words">{lookupResult.business_name}</h2>
                  </div>
                </div>
                <SourceBadge source={lookupResult.source}/>
              </div>

              <div className="mt-5">
                <PreviewField label="Business name" value={lookupResult.business_name}/>
                <PreviewField label="Enterprise number" value={lookupResult.formatted_enterprise_number || lookupResult.enterprise_number} mono/>
                <PreviewField label="Company type" value={lookupResult.company_type}/>
                <PreviewField label="Address" value={lookupResult.registered_address}/>
                <PreviewField label="Website" value={lookupResult.website} mono/>
                <PreviewField label="Contact email" value={lookupResult.contact_email} mono/>
                <PreviewField label="Phone" value={lookupResult.phone} mono/>
                <PreviewField label="Registry status" value={registryStatusLabel(lookupResult.registry_status)}/>
                <PreviewField label="Registry source" value="KBO"/>
                <PreviewField label="Dataset date" value={fmtDate(lookupResult.registry_dataset_date)} mono/>
                <PreviewField label="Fetched" value={fmtDate(lookupResult.registry_fetched_at)} mono/>
              </div>
            </section>

            {error && (
              <p className="text-sm text-red-700">
                {error}
                {workspaceLocked && (
                  <> <Link to="/settings" className="font-medium underline">Go to Workspace verification</Link></>
                )}
              </p>
            )}

            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" onClick={resetSelection}
                className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400">
                Search again
              </button>
              <button type="submit" disabled={loading || lookupLoading || !form.business_name.trim()}
                className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50">
                {loading ? 'Creating...' : 'Create KYB record'}
              </button>
            </div>
          </form>
        )}

        {manualMode && (
          <form onSubmit={submit} className="border border-zinc-200 rounded-sm bg-white p-6">
            <h2 className="font-display text-lg font-semibold">Manual company details</h2>
            <p className="text-sm text-zinc-500 mt-1">Create a KYB record without registry enrichment.</p>

            <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-5 mb-1">Business name</label>
            <input value={form.business_name} onChange={(e) => setField('business_name', e.target.value)} required
              className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"
              placeholder="Acme BV"/>

            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Enterprise number</span>
                <input value={form.enterprise_number} onChange={(e) => setField('enterprise_number', e.target.value)}
                  className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"
                  placeholder="0123.456.789"/>
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Jurisdiction</span>
                <input value={form.jurisdiction} onChange={(e) => setField('jurisdiction', e.target.value)}
                  className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
              </label>
            </div>
            <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-4 mb-1">Phone</label>
            <input value={form.phone} onChange={(e) => setField('phone', e.target.value)}
              className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>

            <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-4 mb-1">Company type</label>
            <input value={form.company_type} onChange={(e) => setField('company_type', e.target.value)}
              className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"
              placeholder="BV, NV, VZW"/>

            <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-4 mb-1">Registered address</label>
            <textarea value={form.registered_address} onChange={(e) => setField('registered_address', e.target.value)}
              className="w-full min-h-24 border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"
              placeholder="Street, postal code, city"/>

            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Website</span>
                <input value={form.website} onChange={(e) => setField('website', e.target.value)}
                  className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"
                  placeholder="https://example.com"/>
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Contact email</span>
                <input value={form.contact_email} onChange={(e) => setField('contact_email', e.target.value)} type="email"
                  className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
              </label>
            </div>

            {error && (
              <p className="mt-3 text-sm text-red-700">
                {error}
                {workspaceLocked && (
                  <> <Link to="/settings" className="font-medium underline">Go to Workspace verification</Link></>
                )}
              </p>
            )}

            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button type="button" onClick={() => { setManualMode(false); setForm(emptyForm); setError(null); }}
                className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400">
                Back to search
              </button>
              <Link to="/dashboard/businesses" className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400">Cancel</Link>
              <button type="submit" disabled={loading || !form.business_name.trim()}
                className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50">
                {loading ? 'Saving...' : 'Create record'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
